import React, {MouseEvent, useState} from "react";
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  SvgIconProps,
  SxProps,
} from "@mui/material";
import MoreVert from "@mui/icons-material/MoreVert";
import {useDownBreakpoint} from "@hooks";
import {Theme} from "@emotion/react";

type MoreAction = {
  label: string;
  icon: React.ElementType<SvgIconProps>;
  onClick: (e: MouseEvent<HTMLElement>) => void;
  color?:
    | "inherit"
    | "primary"
    | "secondary"
    | "success"
    | "error"
    | "info"
    | "warning";
  disabled?: boolean;
};

type MoreActionsProps = {
  actions: MoreAction[];
  buttonSize?: "small" | "medium" | "large";
  responsive?: boolean;
  sx?: SxProps<Theme>;
};

const MoreActions: React.FC<MoreActionsProps> = ({
  actions,
  buttonSize = "small",
  responsive = true,
  sx,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isDownLg = useDownBreakpoint("lg");

  const handleMenuClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(null);
  };

  const handleActionClick = (
    e: MouseEvent<HTMLElement>,
    onClick: (e: MouseEvent<HTMLElement>) => void,
  ) => {
    onClick(e);
    handleClose(e);
  };

  const open = Boolean(anchorEl);

  if (!isDownLg && responsive) {
    return (
      <Stack direction="row" spacing={1} sx={{...sx}}>
        {actions.map((action, index) => {
          const Icon = action.icon;

          return (
            <IconButton
              key={index}
              size={buttonSize}
              color={action.color ?? "primary"}
              disabled={action.disabled}
              aria-label={action.label}
              onClick={action.onClick}
              sx={{textTransform: "none"}}>
              <Icon fontSize={buttonSize} />
            </IconButton>
          );
        })}
      </Stack>
    );
  }

  return (
    <>
      <IconButton size={buttonSize} onClick={handleMenuClick} sx={{...sx}}>
        <MoreVert fontSize={buttonSize} />
      </IconButton>

      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{vertical: "bottom", horizontal: "right"}}
        transformOrigin={{vertical: "top", horizontal: "right"}}>
        {actions.map((action, index) => {
          const Icon = action.icon;

          return (
            <MenuItem
              key={index}
              disabled={action.disabled}
              sx={{p: 0, minHeight: 0}}
              onClick={e => handleActionClick(e, action.onClick)}>
              <ListItemIcon>
                <Icon fontSize={buttonSize} color={action.color} />
              </ListItemIcon>
              <ListItemText>{action.label}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export {MoreActions};
